import React, { ComponentProps } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAnglesUp,
  faEquals,
  faArrowLeft,
  faArrowRotateLeft,
  faArrowRotateRight,
  faArrowUpRightFromSquare,
  faBold,
  faBug,
  faBolt,
  faBullseye,
  faCalendarDays,
  faChartSimple,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircle,
  faCircleInfo,
  faCirclePlay,
  faCircleQuestion,
  faClipboard,
  faClock,
  faCode,
  faComment,
  faDoorOpen,
  faFlagCheckered,
  faFloppyDisk,
  faGripVertical,
  faGripLines,
  faHeading,
  faHouse,
  faHourglassHalf,
  faImage,
  faItalic,
  faLink,
  faListCheck,
  faListOl,
  faListUl,
  faLightbulb,
  faLock,
  faMagnifyingGlass,
  faMaximize,
  faNoteSticky,
  faEllipsisVertical,
  faEnvelope,
  faPaperPlane,
  faPen,
  faPenToSquare,
  faPhone,
  faPlay,
  faPlus,
  faQuoteLeft,
  faRightLeft,
  faRocket,
  faStrikethrough,
  faTable,
  faTag,
  faTriangleExclamation,
  faTrash,
  faUndo,
  faWandMagicSparkles,
  faSquareCheck,
  faStar,
  faXmark,
  faSun,
  faMoon,
  faDesktop,
  faTrophy,
  faBars,
  faUser,
  faGlobe,
  faRightFromBracket,
  faGear,
  faKey,
  faCopy,
  faPlug
} from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck, faCirclePause, faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';

const iconMap = {
  back: faArrowLeft,
  retry: faArrowRotateRight,
  undo: faArrowRotateLeft,
  open: faArrowUpRightFromSquare,
  bolt: faBolt,
  goal: faBullseye,
  calendar: faCalendarDays,
  chart: faChartSimple,
  check: faCheck,
  chevronDown: faChevronDown,
  chevronLeft: faChevronLeft,
  chevronRight: faChevronRight,
  chevronUp: faChevronUp,
  circle: faCircle,
  info: faCircleInfo,
  playCircle: faCirclePlay,
  clipboard: faClipboard,
  clock: faClock,
  door: faDoorOpen,
  finish: faFlagCheckered,
  save: faFloppyDisk,
  grip: faGripVertical,
  home: faHouse,
  hourglass: faHourglassHalf,
  expand: faMaximize,
  lightbulb: faLightbulb,
  search: faMagnifyingGlass,
  list: faListCheck,
  lock: faLock,
  note: faNoteSticky,
  pen: faPen,
  edit: faPenToSquare,
  phone: faPhone,
  play: faPlay,
  plus: faPlus,
  rocket: faRocket,
  mail: faEnvelope,
  more: faEllipsisVertical,
  send: faPaperPlane,
  move: faRightLeft,
  sparkles: faWandMagicSparkles,
  squareCheck: faSquareCheck,
  star: faStar,
  starOutline: faStarRegular,
  suggestion: faComment,
  tag: faTag,
  warning: faTriangleExclamation,
  trash: faTrash,
  undoAlt: faUndo,
  close: faXmark,
  circleCheck: faCircleCheck,
  question: faCircleQuestion,
  pause: faCirclePause,
  // Rich editor toolbar icons
  bold: faBold,
  italic: faItalic,
  strikethrough: faStrikethrough,
  heading: faHeading,
  bulletList: faListUl,
  orderedList: faListOl,
  taskList: faListCheck,
  link: faLink,
  image: faImage,
  codeBlock: faCode,
  blockquote: faQuoteLeft,
  table: faTable,
  horizontalRule: faGripLines,
  sun: faSun,
  moon: faMoon,
  desktop: faDesktop,
  trophy: faTrophy,
  menu: faBars,
  user: faUser,
  globe: faGlobe,
  logout: faRightFromBracket,
  bug: faBug,
  priorityHigh: faAnglesUp,
  priorityMedium: faEquals,
  priorityLow: faAngleDown,
  settings: faGear,
  key: faKey,
  copy: faCopy,
  plug: faPlug,
} as const;

export type IconName = keyof typeof iconMap;

interface IconProps extends Omit<ComponentProps<typeof FontAwesomeIcon>, 'icon'> {
  name: IconName;
}

// Icons that point along the reading direction; mirrored in right-to-left languages (global.css)
const DIRECTIONAL: ReadonlySet<IconName> = new Set<IconName>(['back', 'chevronLeft', 'chevronRight', 'logout']);

const Icon: React.FC<IconProps> = ({ name, className, ...props }) => (
  <FontAwesomeIcon
    icon={iconMap[name]}
    className={DIRECTIONAL.has(name) ? ['icon-rtl-flip', className].filter(Boolean).join(' ') : className}
    {...props}
  />
);

export default Icon;
